import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import i18n from 'i18n';
import { useCallAPI } from 'hooks/useCallAPI';
import { DefaultReportManager } from 'core/report/ReportManager';
import { downloadCsv } from 'utils/DownloadUtils';
import { WeatherType } from 'core/limitation/Limitation';
import {
  formatConditionValue,
  getResultLabel,
  getUnmatchedReasonLabels
} from './weatherVerificationColumns';
import {
  dedupeConditionLabel,
  DIMENSION_TO_WEATHER_TYPE,
  WeatherVerificationCampaignOption,
  WeatherVerificationData,
  WeatherVerificationResult,
  WeatherVerificationStoreOption,
  WeatherVerificationSummary,
  toStoreKey
} from 'core/report/WeatherVerification';

export const DATE_FORMAT = 'YYYY-MM-DD';
export const MAX_QUERY_DAYS = 3;
export const SIZE_PER_PAGE = 50;
/** 表格資料列的 key —— 後端沒有單筆 id，用「整點 + 門市」組出唯一值。 */
export const KEY_COLUMN = 'key';

/** CSV 的天氣維度欄位，與表格上顯示的四欄一致（所見即所得）。 */
const CSV_DIMENSIONS: WeatherType[] = [
  WeatherType.WEATHER_CODE,
  WeatherType.TEMPERATURE,
  WeatherType.RELATIVE_HUMIDITY,
  WeatherType.UV_INDEX
];

const reportManager = new DefaultReportManager();

export type WeatherVerificationQueryOptions = {
  from: string;
  to: string;
  campaignId?: number;
  result?: WeatherVerificationResult;
  storeKeys: string[];
};

export type WeatherVerificationTableRecord = {
  [KEY_COLUMN]: string;
} & WeatherVerificationData['records'][number];

/**
 * 後端只組最近 3 個日曆月的資料，開始日期早於「台北今天往前 3 個月」會回 400。
 * 必須用台北時區算：瀏覽器在其他時區時，跨日那幾個小時會差一天，邊界那天就會選得到卻查不了。
 */
export const QUERY_WINDOW_MONTHS = 3;
const QUERY_TIME_ZONE = 'Asia/Taipei';

/** 台北的今天，轉成與訂單日期同樣解析方式的日期，才能直接比較。 */
export const getTaipeiToday = () =>
  moment(moment.tz(QUERY_TIME_ZONE).format(DATE_FORMAT), DATE_FORMAT);

/**
 * 依訂單走期與可查詢範圍，算出日期選擇器的下限與預設查詢區間。
 * - 下限 = max(走期起日, 台北今天往前 3 個月)
 * - 走期整段早於下限：不能查詢；鎖住的日期框顯示整段走期，讓使用者看得出為什麼查不了
 * - 預設區間：下限之後、走期內「最後 3 天」，且不超過今天；走期尚未開始時退回走期起日當天，API 會回空資料
 */
const getQueryWindow = (startDate: string, endDate: string) => {
  const today = getTaipeiToday();
  const windowStart = moment(today).subtract(QUERY_WINDOW_MONTHS, 'months');
  const orderStart = moment(startDate).startOf('day');
  const orderEnd = moment(endDate).startOf('day');
  const minQueryDate = moment.max(orderStart, windowStart);

  if (orderEnd.isBefore(windowStart)) {
    return {
      minQueryDate: minQueryDate.format(DATE_FORMAT),
      outOfQueryWindow: true,
      defaultRange: {
        from: orderStart.format(DATE_FORMAT),
        to: orderEnd.format(DATE_FORMAT)
      }
    };
  }

  const to = moment.min(orderEnd, today);
  const from = moment.max(
    minQueryDate,
    moment(to).subtract(MAX_QUERY_DAYS - 1, 'days')
  );
  const defaultRange = from.isAfter(to)
    ? {
        from: orderStart.format(DATE_FORMAT),
        to: orderStart.format(DATE_FORMAT)
      }
    : { from: from.format(DATE_FORMAT), to: to.format(DATE_FORMAT) };
  return {
    minQueryDate: minQueryDate.format(DATE_FORMAT),
    outOfQueryWindow: false,
    defaultRange
  };
};

/**
 * 時段篩選的選項。
 *
 * 必須在 render 時才呼叫 —— 若寫成模組層 const，`i18n.t` 會在 import 當下就求值，
 * 早於 `App` 依帳號語系呼叫 `changeLanguage`，label 會被凍結在開機語系。
 */
const getResultFilterOptions = (): SelectOptions[] => [
  { label: i18n.t('weatherVerification.filters.all'), value: '' },
  {
    label: i18n.t('weatherVerification.filters.delivered'),
    value: WeatherVerificationResult.DELIVERED
  },
  {
    label: i18n.t('weatherVerification.filters.weatherUnmatched'),
    value: WeatherVerificationResult.WEATHER_UNMATCHED
  },
  {
    label: i18n.t('weatherVerification.filters.deviceAbnormal'),
    value: WeatherVerificationResult.DEVICE_ABNORMAL
  },
  {
    label: i18n.t('weatherVerification.filters.trafficOccupied'),
    value: WeatherVerificationResult.TRAFFIC_OCCUPIED
  },
  {
    label: i18n.t('weatherVerification.filters.outsideBusinessHours'),
    value: WeatherVerificationResult.OUTSIDE_BUSINESS_HOURS
  }
];

/**
 * 同一支請求連續發出時，只收最後一次的回應。
 * 條件改得快時，舊條件的請求可能比新的晚回來，照順序寫入就會用舊結果蓋掉新的。
 * 每次發請求前取 `next()`，回應回來時用 `isLatest()` 確認還是最新的那一次。
 */
const useLatestRequest = () => {
  const latest = useRef(0);
  return useMemo(
    () => ({
      next: () => ++latest.current,
      isLatest: (requestId: number) => requestId === latest.current
    }),
    []
  );
};

export const exceedsMaxQueryDays = (from: string, to: string) =>
  moment(to).diff(moment(from), 'days') + 1 > MAX_QUERY_DAYS;

export type WeatherVerificationTabModelData = ReturnType<
  typeof useWeatherVerificationTabModel
>;

/**
 * 機台投遞驗證 Tab 的 model。
 *
 * 沿用 `useOutdoorDeviceReportModel` 的做法：篩選用的 state 只負責畫面，
 * 按下「查詢」才 commit 進 `queryOptions`，明細只跟著 `queryOptions` 與頁碼重打。
 *
 * @param active 是否正停在這個 Tab。活動下拉、門市選項與明細都只有在 Tab 被開啟後
 *               才載入，避免使用者從沒切過來也打 API。
 */
export const useWeatherVerificationTabModel = (
  l1ObjectId: number,
  startDate: string,
  endDate: string,
  active: boolean
) => {
  const { loading, callAPIs } = useCallAPI();
  // 統整卡另用一組：整頁 loading 遮罩只跟明細；而且 callAPIs 是 Promise.all，
  // 跟明細放同一批的話彙總一失敗，明細的 callback 也不會執行。
  const { loading: summaryLoading, callAPIs: callSummaryAPIs } = useCallAPI();
  const { minQueryDate, outOfQueryWindow, defaultRange } = useMemo(
    () => getQueryWindow(startDate, endDate),
    [startDate, endDate]
  );

  // 日曆上點的日期先放在 draft，關閉日曆才寫進查詢用的 from/to；
  // 分兩次點起迄日時，才不會先用「只換了起日」的區間打一輪活動與門市（ECOPD-5890）
  const [draftFrom, setDraftFrom] = useState<string>(defaultRange.from);
  const [draftTo, setDraftTo] = useState<string>(defaultRange.to);
  const [from, setFrom] = useState<string>(defaultRange.from);
  const [to, setTo] = useState<string>(defaultRange.to);
  const [campaignId, setCampaignId] = useState<number | undefined>(undefined);
  const [storeKeys, setStoreKeys] = useState<string[]>([]);
  const [result, setResult] = useState<string>('');

  const [campaignOptionRecords, setCampaignOptionRecords] = useState<
    WeatherVerificationCampaignOption[]
  >([]);
  const [storeOptionRecords, setStoreOptionRecords] = useState<
    WeatherVerificationStoreOption[]
  >([]);
  const [data, setData] = useState<WeatherVerificationData | undefined>(
    undefined
  );
  const [page, setPage] = useState<number>(1);
  const [summary, setSummary] = useState<
    WeatherVerificationSummary | undefined
  >(undefined);
  const [summaryError, setSummaryError] = useState<boolean>(false);
  const campaignRequest = useLatestRequest();
  const storeRequest = useLatestRequest();
  const dataRequest = useLatestRequest();
  const summaryRequest = useLatestRequest();
  const [campaignsLoaded, setCampaignsLoaded] = useState<boolean>(false);
  // 目前的活動清單是哪個區間查回來的；門市要等清單跟上目前的區間、活動確認過才查
  const [campaignListRange, setCampaignListRange] = useState<
    { from: string; to: string } | undefined
  >(undefined);

  const [queryOptions, setQueryOptions] =
    useState<WeatherVerificationQueryOptions>({
      from: defaultRange.from,
      to: defaultRange.to,
      campaignId: undefined,
      result: undefined,
      storeKeys: []
    });

  // 錯誤提示看日曆上的日期，關閉前就能提示
  const dayRangeError = useMemo(() => {
    if (exceedsMaxQueryDays(draftFrom, draftTo)) {
      return i18n.t('weatherVerification.errors.maxQueryDays', {
        days: MAX_QUERY_DAYS
      });
    }
    return undefined;
  }, [draftFrom, draftTo]);
  // 擋請求看已寫入的區間：日曆上暫時選超過 3 天時，已寫入區間的門市照樣要能載入，
  // 否則改回原區間再關閉時 from/to 沒變、不會重查，門市就一直是空的
  const queryRangeTooLong = exceedsMaxQueryDays(from, to);

  // 活動清單是不是目前這個區間查回來的。換區間後到新清單回來前，畫面上的活動與門市
  // 還是上一個區間的，不能拿去查新區間 —— 門市清單與查詢按鈕都要等它成立
  const campaignListIsCurrent =
    campaignListRange?.from === from && campaignListRange?.to === to;

  const resultFilterOptions = useMemo(getResultFilterOptions, []);

  const campaignOptions: SelectOptions[] = useMemo(
    () =>
      campaignOptionRecords.map((record) => ({
        label: record.campaignName,
        value: record.campaignId
      })),
    [campaignOptionRecords]
  );

  const storeOptions: SelectOptions[] = useMemo(
    () =>
      storeOptionRecords.map((record) => ({
        label: record.storeName,
        value: toStoreKey(record)
      })),
    [storeOptionRecords]
  );

  // 活動下拉：與門市、明細一致，只有 Tab 被開啟後才載入。
  useEffect(() => {
    // 不打新請求的情況也要取號，讓還在路上的舊請求作廢
    const requestId = campaignRequest.next();
    // 走期整段早於可查詢範圍時，連活動下拉都不載入；門市與明細都要等選出活動，因此也不會打
    if (!active || outOfQueryWindow || queryRangeTooLong) {
      setCampaignListRange(undefined);
      return;
    }
    callAPIs(
      [
        () =>
          reportManager.getWeatherVerificationCampaigns(l1ObjectId, from, to)
      ],
      (records: WeatherVerificationCampaignOption[]) => {
        if (!campaignRequest.isLatest(requestId)) {
          return;
        }
        setCampaignOptionRecords(records);
        setCampaignsLoaded(true);
        setCampaignListRange({ from, to });
        // 區間內沒有活動就不會再查門市，上一個區間的門市選項與已選門市要一起清掉
        if (records.length === 0) {
          setStoreOptionRecords([]);
          setStoreKeys([]);
        }
        // 沿用目前選的活動；若它不在新區間的清單裡就退回第一筆。
        setCampaignId((prev) =>
          prev && records.some((record) => record.campaignId === prev)
            ? prev
            : records[0]?.campaignId
        );
      }
    );
    // queryRangeTooLong 只是 from/to 的衍生值，不需要進依賴
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, outOfQueryWindow, l1ObjectId, from, to, callAPIs]);

  // 門市選項：跟著活動與區間走。換區間時要等新區間的活動清單回來、活動確認過
  // （還在清單內就沿用，不在就換成第一筆，沒有活動就清空）才查，不能先拿上一個活動去查新區間。
  useEffect(() => {
    const requestId = storeRequest.next();
    if (!active || !campaignId || queryRangeTooLong || !campaignListIsCurrent) {
      return;
    }
    callAPIs(
      [
        () =>
          reportManager.getWeatherVerificationStores(
            l1ObjectId,
            campaignId,
            from,
            to
          )
      ],
      (records: WeatherVerificationStoreOption[]) => {
        if (!storeRequest.isLatest(requestId)) {
          return;
        }
        setStoreOptionRecords(records);
        // 換日期區間後門市清單會重抓，已選門市可能不在新清單裡，濾掉避免送出無效的 storeKeys。
        // （換活動時 onCampaignChange 已經先清空已選門市）
        setStoreKeys((prev) =>
          prev.filter((storeKey) =>
            records.some((record) => toStoreKey(record) === storeKey)
          )
        );
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, l1ObjectId, campaignId, from, to, campaignListRange, callAPIs]);

  // 明細：只跟著已 commit 的 queryOptions 與頁碼。
  useEffect(() => {
    const requestId = dataRequest.next();
    if (!active || !queryOptions.campaignId) {
      setData(undefined);
      return;
    }
    callAPIs(
      [
        () =>
          reportManager.getWeatherVerificationData({
            l1ObjectId,
            campaignId: queryOptions.campaignId as number,
            from: queryOptions.from,
            to: queryOptions.to,
            result: queryOptions.result,
            storeKeys: queryOptions.storeKeys,
            page: page - 1 // 畫面 1-based，API 0-based
          })
      ],
      (response: WeatherVerificationData) => {
        if (dataRequest.isLatest(requestId)) {
          setData(response);
        }
      }
    );
  }, [active, l1ObjectId, queryOptions, page, callAPIs, dataRequest]);

  // 統整卡：跟明細一樣只看已 commit 的 queryOptions，但不看頁碼與時段篩選 ——
  // 卡片統計的是整段查詢條件，換頁或只切時段按鈕都不該重打。
  useEffect(() => {
    const requestId = summaryRequest.next();
    setSummary(undefined);
    setSummaryError(false);
    if (!active || !queryOptions.campaignId) {
      return;
    }
    callSummaryAPIs(
      [
        () =>
          reportManager.getWeatherVerificationSummary({
            l1ObjectId,
            campaignId: queryOptions.campaignId as number,
            from: queryOptions.from,
            to: queryOptions.to,
            storeKeys: queryOptions.storeKeys
          })
      ],
      (response: WeatherVerificationSummary) => {
        if (summaryRequest.isLatest(requestId)) {
          setSummary(response);
        }
      },
      // 失敗只在卡片區提示，不跳 toast，也不影響明細
      () => {
        if (summaryRequest.isLatest(requestId)) {
          setSummaryError(true);
        }
      }
    );
  }, [active, l1ObjectId, queryOptions, callSummaryAPIs, summaryRequest]);

  // 活動下拉載入後把預設值帶進 queryOptions，讓使用者切過來就有資料。
  // 日期與時段都用畫面上目前選的：預設區間沒有活動、使用者改了日期（可能也點了時段）之後
  // 才有活動時，帶入的是新區間的活動，配上預設區間的日期或「全部時段」就會跟畫面對不上。
  useEffect(() => {
    if (!campaignId || queryOptions.campaignId) {
      return;
    }
    setQueryOptions((prev) => ({
      ...prev,
      from,
      to,
      result: result ? (result as WeatherVerificationResult) : undefined,
      campaignId
    }));
    // 帶入過一次之後 queryOptions.campaignId 就有值，之後改日期不會再觸發，要按查詢才生效
  }, [campaignId, queryOptions.campaignId, from, to, result]);

  const onDateRangeChange = useCallback((newFrom = '', newTo = '') => {
    setDraftFrom(moment(newFrom).format(DATE_FORMAT));
    setDraftTo(moment(newTo).format(DATE_FORMAT));
  }, []);

  const onDatePickerClosed = useCallback(() => {
    setFrom(draftFrom);
    setTo(draftTo);
  }, [draftFrom, draftTo]);

  // 日曆沒經過關閉就被卸載（切換報表類型、收合篩選區）時，沒確認的選取捨棄，
  // 日期框才會跟實際查詢的區間一致
  const discardDatePicks = useCallback(() => {
    setDraftFrom(from);
    setDraftTo(to);
  }, [from, to]);

  const onResultChange = useCallback((value: string | number) => {
    setResult(value as string);
  }, []);

  const onCampaignChange = useCallback((value: number | undefined) => {
    setCampaignId(value ? +value : undefined);
    setStoreKeys([]);
  }, []);

  const onStoreChange = useCallback((values: string[]) => {
    setStoreKeys(values ?? []);
  }, []);

  const onQueryBtnClick = useCallback(() => {
    if (!campaignId || !campaignListIsCurrent) {
      return;
    }
    setPage(1);
    setQueryOptions({
      from,
      to,
      campaignId,
      // 空字串代表「全部時段」，轉成 undefined 讓 API 層不帶這個參數。
      result: result ? (result as WeatherVerificationResult) : undefined,
      storeKeys
    });
  }, [from, to, campaignId, campaignListIsCurrent, result, storeKeys]);

  const onPageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const tableData: WeatherVerificationTableRecord[] = useMemo(
    () =>
      (data?.records ?? []).map((record) => ({
        ...record,
        [KEY_COLUMN]: `${record.dataTime}_${record.spaceId}`
      })),
    [data]
  );

  /**
   * 匯出 CSV —— spec 要求「所見即所得」，所以只匯出當前這一頁的 50 筆，不額外撈全量。
   * 欄位：時間、門市、活動名稱、結果、各天氣條件實際值、各天氣條件設定值、不符合原因。
   */
  const onExportCsv = useCallback(() => {
    if (tableData.length === 0) {
      return;
    }
    const campaignName = data?.campaign?.campaignName ?? '';
    const dimensionLabel = (weatherType: WeatherType) =>
      i18n.t(`limitation.labels.${weatherType}`);

    const headers = [
      i18n.t('weatherVerification.headers.dataTime'),
      i18n.t('weatherVerification.headers.storeName'),
      i18n.t('weatherVerification.labels.campaign'),
      i18n.t('weatherVerification.headers.result'),
      ...CSV_DIMENSIONS.map(dimensionLabel),
      ...CSV_DIMENSIONS.map((weatherType) =>
        i18n.t('weatherVerification.headers.targetOf', {
          dimension: dimensionLabel(weatherType)
        })
      ),
      i18n.t('weatherVerification.headers.unmatchedReasons')
    ];

    const rows = tableData.map((record) => [
      moment(record.dataTime).format('MM/DD HH:00'),
      record.storeName,
      campaignName,
      getResultLabel(record),
      ...CSV_DIMENSIONS.map((weatherType) =>
        formatConditionValue(weatherType, record.conditions?.[weatherType])
      ),
      ...CSV_DIMENSIONS.map((weatherType) =>
        dedupeConditionLabel(record.conditions?.[weatherType]?.target?.label)
      ),
      getUnmatchedReasonLabels(record.unmatchedReasons).join(',')
    ]);

    const fileName = i18n.t('weatherVerification.labels.csvFileName', {
      campaign: campaignName,
      from: queryOptions.from,
      to: queryOptions.to,
      page
    });
    downloadCsv(fileName, headers, rows);
  }, [tableData, data, queryOptions.from, queryOptions.to, page]);

  /** Campaign header 的條件摘要，例：`天氣現象：晴天 ｜ 溫度：≥28°C`。未設定的維度略過。 */
  const campaignConditionSummary = useMemo(() => {
    const conditions = data?.campaign?.conditions ?? [];
    if (conditions.length === 0) {
      return i18n.t('weatherVerification.labels.noConditions');
    }
    return conditions
      .map((condition) => {
        const weatherType = DIMENSION_TO_WEATHER_TYPE[condition.dimension];
        const dimensionLabel = weatherType
          ? i18n.t(`limitation.labels.${weatherType}`)
          : condition.dimension;
        return `${dimensionLabel}：${dedupeConditionLabel(condition.label)}`;
      })
      .join(' ｜ ');
  }, [data]);

  return {
    loading,
    from,
    to,
    result,
    campaignId,
    storeKeys,
    resultFilterOptions,
    campaignOptions,
    storeOptions,
    campaignsLoaded,
    dayRangeError,
    page,
    totalSize: data?.total ?? 0,
    campaign: data?.campaign,
    campaignConditionSummary,
    tableData,
    minQueryDate,
    outOfQueryWindow,
    canQuery: !dayRangeError && !!campaignId && campaignListIsCurrent,
    draftFrom,
    draftTo,
    onDateRangeChange,
    onDatePickerClosed,
    discardDatePicks,
    onResultChange,
    onCampaignChange,
    onStoreChange,
    onQueryBtnClick,
    onPageChange,
    onExportCsv,
    canExportCsv: tableData.length > 0,
    summary,
    summaryLoading,
    summaryError
  };
};
